<template>
  <div class="hitl-full-embed-widget">
    <div v-if="phase === 'auth'" class="hitl-full-embed-widget__auth">
      <p
        class="hitl-full-embed-widget__auth-hint"
        :class="{ 'hitl-full-embed-widget__auth-hint--error': authError }"
      >
        {{ authError ? authError : 'Completing authorization…' }}
      </p>
    </div>
    <iframe
      v-if="phase === 'embed'"
      ref="hitlFrame"
      allow="autoplay; microphone"
      class="hitl-full-embed-widget__frame"
      :src="hitlOrigin"
      title="HITL Agent UI"
      @load="onIframeLoad"
    />
  </div>
</template>

<script>
import { COOKIE_DOMAIN, COOKIE_PREFIX, HITL_ORIGIN } from '@/constants/env.js';
import { exchangeJwtForOrCookie, isOrCookieTokenAllowed } from '@/utils/orCookieSession.js';
import { createHitlIframeBridge } from '@/utils/hitlIframeBridge.js';

const BUNDLE_MAIN = 'shepherd/index.html';
const VERSION = 'v1.8.1-rc.0';

const HITL_EMBED_URL = `${HITL_ORIGIN}/core/${VERSION}/${BUNDLE_MAIN}?orCookieDomain=${COOKIE_DOMAIN}&orCookieName=${COOKIE_PREFIX}`;

export default {
  name: 'HitlFullEmbedWidget',
  props: {
    jwtToken: {
      type: String,
      default: '',
    },
    /** When true, appends `dev=true` to the HITL iframe query string. */
    dev: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['auth-ready', 'ready', 'hitl-event'],
  data() {
    return {
      phase: 'auth',
      authError: '',
      readyEmitted: false,
      bridge: null,
    };
  },
  computed: {
    hitlOrigin() {
      return this.dev ? `${HITL_EMBED_URL}&dev=true` : HITL_EMBED_URL;
    },
  },
  beforeUnmount() {
    this.bridge?.destroy();
    this.bridge = null;
  },
  async mounted() {
    const tokenOk = await isOrCookieTokenAllowed();
    if (tokenOk) {
      this.phase = 'embed';
      this.$emit('auth-ready');
      return;
    }

    const jwt = (this.jwtToken || '').trim();
    if (!jwt) {
      this.authError =
        'No valid or session and no JWT was supplied. Provide jwtToken (or sign in so an or cookie exists).';
      console.error(this.authError);
      return;
    }

    try {
      await exchangeJwtForOrCookie(jwt);
      const allowed = await isOrCookieTokenAllowed();
      if (!allowed) {
        this.authError =
          'The server returned a session, but it was not accepted by the token check. Try a fresh JWT.';
        console.error(this.authError);
        return;
      }
    } catch (e) {
      const msg = e && e.message ? String(e.message) : String(e);
      this.authError = `Authorization failed: ${msg}`;
      console.error('[HitlFullEmbedWidget] JWT exchange failed', e);
      return;
    }

    this.phase = 'embed';
    this.$emit('auth-ready');
  },
  methods: {
    onIframeLoad() {
      if (this.phase !== 'embed' || this.readyEmitted) return;

      const iframe = this.$refs.hitlFrame;
      if (!iframe) return;

      this.bridge?.destroy();
      this.bridge = createHitlIframeBridge(iframe, {
        targetOrigin: HITL_ORIGIN,
        onEvent: (payload) => {
          console.log('[full-HITL → host]', payload.message, payload.params);
          this.$emit('hitl-event', payload);
        },
      });

      this.readyEmitted = true;
      this.bridge.ping();
      this.$emit('ready', this.bridge);
    },
  },
};
</script>

<style scoped lang="scss">
.hitl-full-embed-widget {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
  z-index: 1;

  &__frame {
    flex: 1;
    min-height: 0;
    width: 100%;
    border: 0;
    display: block;
  }

  &__auth {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 12rem;
    padding: 24px;
    box-sizing: border-box;
  }

  &__auth-hint {
    margin: 0;
    font-size: 14px;
    color: #444;
    text-align: center;

    &--error {
      color: #c00;
    }
  }
}
</style>
